import { GENDER } from '../pedigree-tool/pedigree/config';

const SEX_TO_GENDER = { MALE: GENDER.male, FEMALE: GENDER.female };

// Synthetic: a real uid is a Mongo _id. Safe only because nothing is saved.
export const previewUid = (pid) => `sketch-${pid}`;

// Coerced exactly as /apply's `asPid` does, so a stringified id is not dropped
// on one side only.
const toPid = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const isPid = (v) => toPid(v) !== null;

// Parent slot wins, then the stated `sex`. Same order as the backend.
export function genderByPid(people) {
  const gender = new Map(
    people.map((p) => [
      toPid(p.pid),
      SEX_TO_GENDER[String(p.sex).toUpperCase()] || GENDER.unknown
    ])
  );
  for (const person of people) {
    const father = toPid(person.fatherId);
    const mother = toPid(person.motherId);
    if (father !== null) gender.set(father, GENDER.male);
    if (mother !== null) gender.set(mother, GENDER.female);
  }
  return gender;
}

const cleanIds = (list, self) =>
  Array.isArray(list)
    ? [...new Set(list.map(toPid).filter((id) => id !== null && id !== self))]
    : [];

// Links are not repaired here: /generate already returns the repaired config,
// and a second copy would drift into drawing a different pedigree.
// Other-only people draw grey.
export function toLayoutFamily(config, statusOf = () => 'unchecked') {
  const people = (config?.people || []).filter((p) => isPid(p?.pid));
  const gender = genderByPid(people);

  // Uncoded phrases are recorded as Other Disease/Comment, so any condition fills.
  const fillFor = (conditions) => {
    if (!conditions.length) return null;
    return conditions.every((c) => statusOf(c) === 'other') ? 'other' : 'coded';
  };

  return people.map((rawPerson) => {
    const person = { ...rawPerson, pid: toPid(rawPerson.pid) };
    const conditions = Array.isArray(person.conditions)
      ? person.conditions.filter(Boolean)
      : [];
    const name = [person.firstName, person.lastName]
      .filter(Boolean)
      .join(' ')
      .trim();

    return {
      pid: person.pid,
      uid: previewUid(person.pid),
      // No UPN yet; PedigreePerson falls back to pid, the right ordering.
      upn: 0,
      gender: gender.get(person.pid) || GENDER.unknown,
      name,
      // UNKNOWN is the absence of a statement, not a claim of life.
      isDeceased: String(person.lifeStatus).toUpperCase() === 'DECEASED',
      fatherId: toPid(person.fatherId),
      motherId: toPid(person.motherId),
      spouseIds: cleanIds(person.spouseIds, person.pid),
      childrenIds: cleanIds(person.childrenIds, person.pid),
      siblingIds: cleanIds(person.siblingIds, person.pid),
      // Render-only. Real colours are allocated server-side at apply.
      affected: conditions.length > 0,
      fill: fillFor(conditions)
    };
  });
}
